import {
  StackDivider,
  TabPanel,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { useMutation, useQuery } from "@tanstack/react-query";
import type {
  FeaturePreferenceKey,
  FeaturePreferences,
} from "models/types/agency-settings";
import { useForm } from "react-hook-form";
import EnquirySourceChart from "assets/help-images/enquiry-source-chart.png";
import InterestLevelChart from "assets/help-images/interest-level-chart.png";
import PriceIndicationChart from "assets/help-images/price-indication-source-chart.png";
import ReachMarketing from "assets/help-images/reach-marketing.png";
import ShowOffer from "assets/help-images/show-offer.png";
import VendorPresentation from "assets/help-images/vendor-presentation.png";
import { UnexpectedErrorContent } from "components/errors/components/unexpected-error-content";
import { Form } from "components/form";
import { HelpPopover } from "components/help-popover";
import { LoadingScreen } from "components/loading-screen";
import { AgencySettingsPageCards } from "modules/agency-settings/components/agency-settings-page/agency-settings-page-cards";
import { featurePreferencesModel } from "modules/agency-settings/models/feature-preferences-model";
import { PreferenceGroup } from "modules/agency-settings/screens/feature-preferences/components/preference-group";
import {
  PreferenceSwitch,
  type PreferenceSwitchProps,
} from "modules/agency-settings/screens/feature-preferences/components/preference-switch";
import { getTraceId } from "utils/get-trace-id";

const toFormKey = (key: FeaturePreferenceKey) => key.replace(/\./g, "-");

const toDataKey = (key: string) =>
  key.replace(/-/g, ".") as FeaturePreferenceKey;

const PREFERENCES: Record<string, PreferenceSwitchProps> = {
  showOffers: {
    name: toFormKey("vendor_portal.navigation.show_offers"),
    label: "Show offers",
    helperPopup: (
      <HelpPopover
        title={"Contract offers"}
        image={ShowOffer}
        description={
          "Choose to share offers received from buyers with your vendors."
        }
      />
    ),
  },
  showVendorPresentation: {
    name: toFormKey(
      "vendor_portal.functionality.presentation_mode.show_vendor_presentation",
    ),
    label: "Show vendor presentation",
    helperPopup: (
      <HelpPopover
        title={"Vendor presentation"}
        image={VendorPresentation}
        description={
          "The vendor portal will show a summary of activity for your clients in a presentation mode."
        }
      />
    ),
  },
  showMarketingCosts: {
    name: toFormKey(
      "vendor_portal.functionality.marketing.show_marketing_costs",
    ),
    label: "Show marketing costs",
  },
  allowMarketingFeedback: {
    name: toFormKey(
      "vendor_portal.functionality.marketing.allow_market_feedback_and_approvals",
    ),
    label: "Allow marketing feedback & approvals",
  },
  promoteReachMarketing: {
    name: toFormKey(
      "vendor_portal.functionality.performance.promote_reach_marketing",
    ),
    label: "Promote Reach marketing",
    helperPopup: (
      <HelpPopover
        title={"Reach marketing"}
        image={ReachMarketing}
        description={
          "This is an opportunity to educate the vendor about Reach marketing and the associated benefits by opting in."
        }
      />
    ),
  },
  showEnquiriesSourceChart: {
    name: toFormKey(
      "vendor_portal.functionality.graphs.show_enquiries_source_chart",
    ),
    label: "Show enquiries source chart",
    helperPopup: (
      <HelpPopover
        title={"Enquiry source chart"}
        image={EnquirySourceChart}
        description={
          "Choose to share data about where most enquiries are coming from."
        }
      />
    ),
  },
  showPriceIndicationChart: {
    name: toFormKey(
      "vendor_portal.functionality.graphs.show_price_indicator_chart",
    ),
    label: "Show price indication chart",
    helperPopup: (
      <HelpPopover
        title={"Price indication source chart"}
        image={PriceIndicationChart}
        description={
          "Choose to share data on what buyers think the property is worth based on price indications given for the listing."
        }
      />
    ),
  },
  showInterestLevelData: {
    name: toFormKey(
      "vendor_portal.functionality.interest_level.show_interest_level_data",
    ),
    label: "Show interest level data",
    helperPopup: (
      <HelpPopover
        title={"Interest level"}
        image={InterestLevelChart}
        description={
          "Choose to share buyer interest level with your vendors. This governs the interest level chart, the hot/warm/cold/neutral labels on feedback and attendees, and interest-level filtering."
        }
      />
    ),
  },
  revealInitialOfInterestedParties: {
    name: toFormKey(
      "vendor_portal.privacy.reveal_last_name_initial_of_interested_parties",
    ),
    label: "Reveal last name initial for interested parties",
  },
};

type VendorPortalTabWithDataProps = {
  data: FeaturePreferences;
};

function VendorPortalTabWithData({ data }: VendorPortalTabWithDataProps) {
  const toast = useToast();

  const form = useForm<FeaturePreferences>({
    defaultValues: Object.entries(data).reduce(
      (acc, [key, value]) => {
        acc[toFormKey(key as FeaturePreferenceKey)] = value;
        return acc;
      },
      {} as Record<string, boolean>,
    ),
  });

  const updateFeaturePreferencesMutation = useMutation({
    ...featurePreferencesModel.mutations.update(),
    onSuccess: () => {
      toast({
        title: "Preferences updated",
        description: "Your feature preferences have been updated successfully.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    },
    onError: () => {
      toast({
        title: "Update failed",
        description: (
          <>
            <Text>
              {" "}
              There was an error updating your feature preferences. Please try
              again.
            </Text>
            <small>Trace ID: {getTraceId()}</small>
          </>
        ),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    },
    meta: {
      showErrorModal: false,
    },
  });

  const onSubmit = async (value: Record<string, boolean>) => {
    const data = Object.entries(value).reduce((acc, [key, value]) => {
      acc[toDataKey(key)] = value;
      return acc;
    }, {} as FeaturePreferences);

    await updateFeaturePreferencesMutation.mutateAsync(data);

    form.reset(value);
  };

  return (
    <TabPanel>
      <Form
        form={form}
        id={"feature-preferences"}
        onSubmit={onSubmit}
        enableSaveCancelOverlay
      >
        <VStack gap={6}>
          <AgencySettingsPageCards
            heading={"Navigate"}
            description={"Show or hide sections in the navigation menu"}
          >
            <PreferenceSwitch {...PREFERENCES.showOffers} />
          </AgencySettingsPageCards>
          <AgencySettingsPageCards
            heading={"Functionality"}
            description={"Show or hide features in the portal"}
          >
            <VStack gap={6} divider={<StackDivider />}>
              <PreferenceGroup heading={"Marketing"}>
                <PreferenceSwitch {...PREFERENCES.showMarketingCosts} />
                <PreferenceSwitch {...PREFERENCES.allowMarketingFeedback} />
              </PreferenceGroup>
              <PreferenceGroup heading={"Presentation Mode"}>
                <PreferenceSwitch {...PREFERENCES.showVendorPresentation} />
              </PreferenceGroup>
              <PreferenceGroup heading={"Performance"}>
                <PreferenceSwitch {...PREFERENCES.promoteReachMarketing} />
              </PreferenceGroup>
              <PreferenceGroup heading={"Graphs"}>
                <PreferenceSwitch {...PREFERENCES.showEnquiriesSourceChart} />
                <PreferenceSwitch {...PREFERENCES.showPriceIndicationChart} />
              </PreferenceGroup>
              <PreferenceGroup heading={"Interest level"}>
                <PreferenceSwitch {...PREFERENCES.showInterestLevelData} />
              </PreferenceGroup>
            </VStack>
          </AgencySettingsPageCards>
          <AgencySettingsPageCards
            heading={"Privacy"}
            description={"Adjust privacy preferences"}
          >
            <PreferenceSwitch
              {...PREFERENCES.revealInitialOfInterestedParties}
            />
          </AgencySettingsPageCards>
        </VStack>
      </Form>
    </TabPanel>
  );
}

export function VendorPortalTab() {
  const { data, isLoading, error } = useQuery(
    featurePreferencesModel.queries.get(),
  );

  if (error) {
    return <UnexpectedErrorContent error={error} />;
  }

  if (!data || isLoading) {
    return <LoadingScreen loadingText={"Loading feature preferences..."} />;
  }

  return <VendorPortalTabWithData data={data} />;
}
